import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  OnDestroy,
  Output,
  forwardRef,
  inject
} from '@angular/core';
import { Component, ElementRef, Input, OnInit, Renderer2, ViewChild, AfterViewInit } from '@angular/core';
import { FormGroup, FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { MessageService } from 'primeng/api';
import Quill from 'quill';
import { GlobalConstants } from 'src/app/base/global.constants';

// --- ChipBlot definition and registration ---
const EmbedBlot = Quill.import('blots/embed');
class ChipBlot extends EmbedBlot {
  static blotName = 'chip';
  static tagName = 'div';
  static create(value: string): HTMLElement {
    const node = super.create() as HTMLElement;
    node.setAttribute('contenteditable', 'false');
    node.classList.add('chip');
    let label = value;
    if (!GlobalConstants.variablePatternTest.test(label)) {
      label = '${' + label + '}';
    }
    node.innerText = label;
    node.style.userSelect = 'none';
    node.style.pointerEvents = 'none';
    node.style.background = '#e0e0e0';
    node.style.borderRadius = '16px';
    node.style.padding = '2px 8px';
    node.style.display = 'inline-block';
    node.style.margin = '0 2px';
    return node;
  }
  static value(node: HTMLElement): string {
    return node.innerText;
  }
}
Quill.register(ChipBlot);
import { ApiConstants } from 'src/app/base/api-constants';
import { getWorkflowMail, saveWorkflowMail, setWorkflowMail } from 'src/app/STORE/actions/workflow.actions';
import { workflowMail } from 'src/app/STORE/selectors/workflow.selector';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { rolesTable } from 'src/app/STORE/selectors/roles.selector';
import { cloneDeep } from 'lodash-es';
import { getImpMail, saveImpMail } from 'src/app/STORE/actions/mailinfo.actions';
import { impMail } from 'src/app/STORE/selectors/mailinfo.selector';
import { TranslateService } from '@ngx-translate/core';
import { data } from 'jquery';

@Component({
  selector: 'vb-mail-template',
  templateUrl: './mail-template.component.html',
  styleUrls: ['./mail-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MailTemplateComponent),
      multi: true
    }
  ],
  standalone: false //NOSONAR
})
export class MailTemplateComponent implements OnInit, OnDestroy, AfterViewInit {
  private renderer = inject(Renderer2);
  private appGlobalService = inject(AppGlobalService);
  private ref = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);
  private messageService = inject(MessageService);
  private store = inject(Store);
  private translate = inject(TranslateService);

  @Input() element: any;
  @Input() disableField = false;
  @Input() formDetails: any = null;
  @ViewChild('editorDiv') editorDiv: ElementRef;
  private modelValue: any;
  mailTemplate = '';
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  toRecipients = true;
  formGroup: FormGroup = new FormGroup({
    to: new FormControl([], [Validators.required]),
    cc: new FormControl([]),
    subject: new FormControl('', [Validators.required]),
    description: new FormControl('')
  });
  showMailTemplate = false;
  mailObj: any = null;
  message: any;
  messages: any = [];
  errorFlag = false;
  subjectText = '';
  descriptionText = '';
  descriptionValueText = '';
  nextActor: any = [];
  // public Editor = DecoupledEditor;
  initialValue: any;
  genders: string[] = ['Male', 'Female'];
  isEmailValid = false;
  @Input() actorsData: any[] = [];
  @Input() fields: any[] = [];
  @Input() curNode: any;
  @Input() dataFrom = '';
  options: any[];
  allRoles: any = [];
  editorText: any;
  quill: any;
  isUpdate = false;
  sid: any;
  isLoading = false;
  destroy$: Subject<boolean> = new Subject<boolean>();
  @Output() public opValue = new EventEmitter();
  appMetaInfo: any;
  warning = '';
  warnFlag = false;
  iconLoaded = false;
  roles: any[] = [];

  ngOnInit(): void {
    this.appMetaInfo = this.appGlobalService.get('appmetaInfo');
    if (this.dataFrom === 'import' || this.dataFrom === 'export') {
      this.store.select(rolesTable).subscribe((allTables: any) => {
        if (allTables) {
          this.allRoles = cloneDeep(allTables.rolesTable);
        }
        if (this.allRoles) {
          this.options = this.allRoles;
          this.options.forEach((obj) => {
            obj.actName = obj.shortName;
          });
        }
      });
    } else {
      this.options = this.actorsData.length ? [...this.actorsData[0].items, ...this.actorsData[1].items] : [];
    }
    this.defaultText();
    if (!this.dataFrom) {
      this.store
        .select(workflowMail)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          if (res) {
            this.isLoading = false;
            let obj;
            if (res.sid) {
              this.isUpdate = true;
              this.sid = res.sid;
              this.mailObj = res;
              obj = { ...res };
              if (res.toRecipients) {
                try {
                  obj.toRecipients = JSON.parse(res.toRecipients)
                    ? this.getActorArray(JSON.parse(res.toRecipients))
                    : null;
                } catch (e) {
                  console.log(e);
                }
              }
              if (res.ccRecipients) {
                try {
                  obj.ccRecipients = JSON.parse(res.ccRecipients)
                    ? this.getActorArray(JSON.parse(res.ccRecipients))
                    : null;
                } catch (e) {
                  console.log(e);
                }
              }
            } else {
              this.isUpdate = false;
              this.sid = '';
              obj = '';
            }
            this.defaultText();
            this.setValueToMail(obj);
          }
        });
    } else {
      this.store
        .select(impMail)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          if (res) {
            this.isLoading = false;
            let obj;
            if (res.sid) {
              this.isUpdate = true;
              this.sid = res.sid;
              this.mailObj = res;
              obj = { ...res };
              if (res.toRecipients) {
                try {
                  obj.toRecipients = JSON.parse(res.toRecipients)
                    ? this.getActorArray(JSON.parse(res.toRecipients))
                    : null;
                } catch (e) {
                  console.log(e);
                }
              }
              if (res.ccRecipients) {
                try {
                  obj.ccRecipients = JSON.parse(res.ccRecipients)
                    ? this.getActorArray(JSON.parse(res.ccRecipients))
                    : null;
                } catch (e) {
                  console.log(e);
                }
              }
              if (res.body) {
                obj.description = res.body;
              }
            } else {
              this.isUpdate = false;
              this.sid = '';
              obj = '';
            }
            this.defaultText();
            this.setValueToMail(obj);
          }
        });
    }
    if (this.dataFrom === 'import') {
      this.roles = this.options ? [...this.options] : [];
    }
  }

  get model() {
    return this.modelValue;
  }

  set(value: any) {
    this.modelValue = value;
    this.onChange(this.modelValue);
  }

  writeValue(value: any): void {
    this.modelValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  defaultText() {
    this.nextActor = [];
    if (this.element && this.curNode && !this.dataFrom) {
      const statusTo = this.appMetaInfo[this.element?.statusTo] || '';
      this.subjectText = `Workflow changed from ${this.curNode?.parent?.parent?.data?.name} to ${statusTo}`;
      this.descriptionText = `Dear User
      Workflow changed from ${this.curNode?.parent?.parent?.data?.name} to ${statusTo}`;
      try {
        console.log(this.element.nextActor);
        const nxtActJSON = JSON.parse(this.element.nextActor);
        if (nxtActJSON.length > 0) {
          nxtActJSON.forEach((act) => {
            if (act.value) {
              const values = act.value.split(',');
              const actor = this.options.filter((d) => {
                return values.includes(d.sid);
              });
              this.nextActor = [...this.nextActor, ...actor];
            }
            // else {
            //   this.nextActor = [];
            // }
          });
        } else {
          this.nextActor = [];
        }
      } catch (e) {
        console.log(e);
        this.nextActor = [];
      }
    } else if (this.dataFrom) {
      if (this.dataFrom === 'import') {
        this.subjectText = `${this.element.name} imported successfully`;
      } else if (this.dataFrom === 'export') {
        this.subjectText = `${this.element.name}  Data Exported Successfully`;
      }
    }
  }

  onAddRecChange(event) {
    const toRec = this.formGroup.get('to')?.value;
    const ccRec = this.formGroup.get('cc')?.value;
    if (!toRec || !ccRec) {
      this.warnFlag = false;
      this.warning = '';
      return;
    }
    const comm = ccRec
      .filter((cc) => toRec.find((to) => (to?.sid && cc?.sid && to.sid === cc.sid) || (typeof to === 'string' && typeof cc === 'string' && to === cc)))
      .map((rec) => rec?.actName || rec)
      .join(',');
    if (comm.length) {
      this.warnFlag = true;
      this.warning = `${comm} is also used in cc`;
    } else {
      this.warnFlag = false;
      this.warning = '';
    }
  }

  onCChange(event) {
    const toRec = this.formGroup.get('to')?.value;
    const ccRec = this.formGroup.get('cc')?.value;
    if (!toRec || !ccRec) {
      this.warnFlag = false;
      this.warning = '';
      return;
    }
    const comm = toRec
      .filter((to) => ccRec.find((cc) => (cc?.sid && to?.sid && cc.sid === to.sid) || (typeof cc === 'string' && typeof to === 'string' && cc === to)))
      .map((rec) => rec?.actName || rec)
      .join(',');
    if (comm.length) {
      this.warnFlag = true;
      if (this.dataFrom) {
        this.warning = `${comm} is also used in Additional Recipients`;
      } else {
        this.warning = `${comm} is also used in To`;
      }
    } else {
      this.warnFlag = false;
      this.warning = '';
    }
  }

  getActorArray(recipients) {
    let actors: any = [];
    recipients.forEach((act) => {
      if (act.type === 'role' || act.type === 'field') {
        const sids = act.value.split(',');
        const arr = this.options.filter((opt) => {
          return sids.includes(opt.sid);
        });
        actors = [...actors, ...arr];
      } else if (act.type === 'custom') {
        actors = [...actors, ...act.value.split(',')];
      }
    });
    return actors;
  }

  openMailTemplate() {
    this.store.dispatch(setWorkflowMail({ workflowMail: null }));
    this.showMailTemplate = true;
    if (!this.dataFrom) {
      const apiConstants = ApiConstants.getWorkflowMailById;
      this.store.dispatch(
        getWorkflowMail({
          apiConstants,
          params: { stepid: this.element.sid, appid: this.appGlobalService.get('appId') }
        })
      );
      this.isLoading = true;
    } else {
      this.store
        .select(rolesTable)
        .pipe(takeUntil(this.destroy$))
        .subscribe((allTables: any) => {
          if (allTables) {
            const roles = cloneDeep(allTables.results);
            this.options = roles.map((rol) => {
              return { ...rol, actName: rol.name };
            });
          }
        });
      const apiConstants = ApiConstants.getImpMailById;
      this.store.dispatch(
        getImpMail({
          apiConstants,
          params: { entityId: this.element.sid, appId: this.appGlobalService.get('appId') }
        })
      );
      this.isLoading = true;
    }
    const actors = this.options.map((element) => element.actName);
    setTimeout(() => {
      const _self = this;
      let fieldNames: any = [];
      // Move set-as-variable button out of toolbar before destroying it, so it survives
      const existingBtn = document.querySelector('.ql-toolbar .set-as-variable');
      if (existingBtn) {
        const textEditor = existingBtn.closest('.text-editor') || existingBtn.closest('.mail-template-modal');
        if (textEditor) {
          textEditor.appendChild(existingBtn);
        }
      }
      $('.ql-toolbar').remove();
      if (this.dataFrom === 'import') {
        fieldNames = actors;
      } else {
        fieldNames = Array.isArray(this.fields)
          ? this.fields
              .filter(
                (f) => f.f_type.toLowerCase() !== 'image carousel' && f.f_type.toLowerCase() !== 'file attachments'
              )
              .map((f) => f.f_fieldId)
          : Object.values(this.fields)
              .filter(
                (f: any) => f.f_type.toLowerCase() !== 'image carousel' && f.f_type.toLowerCase() !== 'file attachments'
              )
              .map((f: any) => f.f_fieldId);
      }
      _self.quill = new Quill('#editor', {
        theme: 'snow',
        modules: {
          clipboard: {
            matchers: [
              ['div.chip', function(_node, delta) {
                const text = _node.textContent || '';
                const cleanText = text.replace(GlobalConstants.variablePatternExact, '$1');
                return { ops: [{ insert: cleanText }] };
              }],
              [Node.ELEMENT_NODE, function(_node, delta) {
                if (delta && delta.ops) {
                  delta.ops = delta.ops.map(op => {
                    if (op.insert && typeof op.insert === 'string') {
                      op.insert = op.insert.replace(GlobalConstants.variablePatternGlobal, '$1');
                    }
                    return op;
                  });
                }
                return delta;
              }]
            ]
          },
          toolbar: {
            container: [
              ['bold', 'italic', 'underline', 'strike'], // toggled buttons
              ['blockquote', 'code-block'],

              [{ header: 1 }, { header: 2 }], // custom button values
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
              [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
              [{ direction: 'rtl' }], // text direction

              [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
              [{ header: [1, 2, 3, 4, 5, 6, false] }],

              [{ color: [] }, { background: [] }], // dropdown with defaults from theme
              [{ font: [] }],
              [{ align: [] }],
              ['clean'],
              [{ placeholder: fieldNames }] // my custom dropdown                                   // remove formatting button
            ],
            handlers: {
              placeholder: function (value) {
                if (value) {
                  const cursorPosition = this.quill.getSelection()?.index || 0;
                  console.log('Before insertEmbed', value, cursorPosition);
                  this.quill.insertEmbed(cursorPosition, 'chip', value, Quill.sources.USER);
                  console.log('After insertEmbed');
                  this.quill.setSelection(cursorPosition + 1);
                }
              }
            }
          }
        }
      });

      // Add paste event listener to strip ${} patterns from pasted content
      _self.quill.root.addEventListener('paste', function(e) {
        e.preventDefault();
        const clipboardData = e.clipboardData || (window as any).clipboardData;
        let pastedText = clipboardData.getData('text/plain') || clipboardData.getData('text');
        if (!pastedText) return;
        // Remove BOM/ZWNBSP characters (U+FEFF)
        pastedText = pastedText.replace(GlobalConstants.bomPattern, '');
        // Remove ${...} wrapper patterns from pasted text
        pastedText = pastedText.replace(GlobalConstants.variablePatternGlobal, '$1');
        // Get selection or use end of document
        let index = _self.quill.getLength() - 1;
        const selection = _self.quill.getSelection(true);
        if (selection) {
          index = selection.index;
        }
        _self.quill.insertText(index, pastedText, Quill.sources.USER);
        _self.quill.setSelection(index + pastedText.length);
      });

      // We need to manually supply the HTML content of our custom dropdown list
      const placeholderPickerItems = Array.prototype.slice.call(
        document.querySelectorAll('.ql-placeholder .ql-picker-item')
      );
      placeholderPickerItems.forEach((item) => (item.textContent = item.dataset.value));

      const placeholderLabel = document.querySelector('.ql-placeholder .ql-picker-label');
      if (placeholderLabel) {
        // Focus the editor when the picker is opened so Quill's internal handler works on first click
        placeholderLabel.addEventListener('mousedown', () => {
          _self.quill.focus();
        });
        if (this.dataFrom === 'import') {
          placeholderLabel.innerHTML = `Insert Role ${placeholderLabel.innerHTML}`;
        } else {
          placeholderLabel.innerHTML = `Insert Field ${placeholderLabel.innerHTML}`;
        }
      }

      // Insert a line break in the toolbar before the picker, then place Set as Variable after it
      const toolbar = document.querySelector('.mail-template-modal .ql-toolbar');
      const placeholderPicker = document.querySelector('.mail-template-modal .ql-toolbar .ql-placeholder');
      const placeholderFormats = placeholderPicker?.closest('.ql-formats');
      const setAsVarBtn = document.querySelector('.mail-template-modal .set-as-variable');
      if (toolbar && placeholderFormats && setAsVarBtn) {
        const lineBreak = document.createElement('div');
        lineBreak.className = 'toolbar-line-break';
        toolbar.insertBefore(lineBreak, placeholderFormats);
        placeholderFormats.after(setAsVarBtn);
      }

      const bindingText = this.formGroup.get('description')?.value;
      _self.quill.setText(bindingText);
    }, 100);
  }

  getActorDataJSON(actorValue, isToRecipient?: boolean) {
    let actorData = [
        {
          type: 'role',
          value: ''
        },
        {
          type: 'custom',
          value: ''
        }
      ],
      actor: any = [];
    let actorInitiator;
    if (isToRecipient && (this.dataFrom === 'import' || this.dataFrom === 'export')) {
      actorInitiator = {
        type: 'keyword',
        value: 'initiator'
      };
    }
    if (actorValue && actorValue.length > 0) {
      if (!this.dataFrom) {
        actorData = [
          ...actorData,
          {
            type: 'field',
            value: ''
          }
        ];
      }
      actorValue.forEach((act) => {
        if (Object.prototype.hasOwnProperty.call(act, 'shortName')) {
          actorData[0].value = `${actorData[0].value + act.sid},`;
        } else if (Object.prototype.hasOwnProperty.call(act, 'name')) {
          actorData[2].value = `${actorData[2].value + act.sid},`;
        } else if (Object.prototype.hasOwnProperty.call(act, 'custom')) {
          actorData[1].value = act.sid;
        } else {
          actorData[1].value = `${actorData[1].value + act},`;
        }
      });
      actorData[0].value =
        actorData[0].value.length > 0 ? actorData[0].value.substring(0, actorData[0].value.length - 1) : '';
      actorData[1].value =
        actorData[1].value.length > 0 ? actorData[1].value.substring(0, actorData[1].value.length - 1) : '';
      actorData.forEach((act) => {
        if (act.value) {
          actor = [...actor, act];
        }
      });
    }
    if (actorInitiator) {
      actor = [actorInitiator, ...actor];
    }
    return actor.length ? JSON.stringify(actor) : '';
  }

  formInsertFields() {
    let inserFlds: any = [],
      inserRoles: any = [];
    const descTxt = this.editorDiv.nativeElement.innerText;
    const arr = descTxt?.split('${');
    if (arr && arr.length) {
      arr.forEach((a, ind) => {
        if (ind !== 0) {
          const i = descTxt.indexOf(a);
          const j = a.indexOf('}');
          if (j > -1) {
            const str = a.substring(0, j);
            if (str.includes('$') || str.includes('{')) {
              this.messages.push(this.translate.instant('INVALID_VARIABLE'));
            }
            console.log(this.fields);
            const field = this.fields.find((opt) => opt.f_fieldId === str);
            if (field && !inserFlds.includes(field.sid)) {
              inserFlds = [...inserFlds, field.sid];
            }
            const role = this.options.find((opt) => opt.shortName === str);
            if (role && !inserRoles.includes(role.sid)) {
              inserRoles = [...inserRoles, role.sid];
            } else {
              const fld = this.options.find((opt) => opt.name === str);
              if (fld && !inserFlds.includes(fld.sid)) {
                inserFlds = [...inserFlds, fld.sid];
              }
            }
          } else if (ind !== arr.length) {
            this.messages.push(this.translate.instant('INVALID_VARIABLE'));
          }
        }
      });
    }
    if (this.dataFrom) {
      return {
        insertRoles: inserRoles.join(','),
        oldInsertRoles: inserFlds !== this.mailObj?.insertRoles ? this.mailObj?.insertRoles : undefined
      };
    }
    return {
      insertFields: inserFlds.join(','),
      insertRoles: inserRoles.join(','),
      oldInsertFields: inserFlds !== this.mailObj?.insertFields ? this.mailObj?.insertFields : undefined,
      oldInsertRoles: inserFlds !== this.mailObj?.insertRoles ? this.mailObj?.insertRoles : undefined
    };
  }

  onSave() {
    // Extract chip values and text from Quill editor before saving
    if (this.quill) {
      const delta = this.quill.getContents();
      let result = '';
      delta.ops.forEach((op) => {
        if (op.insert && typeof op.insert === 'object' && op.insert.chip) {
          let chipLabel = op.insert.chip;
          result += chipLabel;
        } else if (op.insert && typeof op.insert === 'string') {
          result += op.insert;
        }
      });
      // Remove BOM/ZWNBSP characters (U+FEFF) that Quill adds around embeds
      result = result.replace(GlobalConstants.bomPattern, '');
      this.descriptionValueText = result;
      this.formGroup.patchValue({
        description: this.descriptionValueText
      });
    }
    this.message = '';
    this.messages = [];
    this.errorFlag = false;
    if ((!this.formGroup.get('to')?.value || this.formGroup.get('to')?.value.length === 0) && !this.dataFrom) {
      this.messages.push('To field is required');
    }
    if (!this.formGroup.get('subject')?.value) {
      this.messages.push('Subject is required');
    }
    const insertFlds = this.formInsertFields();
    this.message = this.messages?.join('<br/>');
    this.messages = [];
    if (this.message.length) {
      this.errorFlag = true;
      // this.showMessage('error', this.message);
      return;
    }

    this.errorFlag = false;

    const formData = {
      to: this.formGroup.get('to')?.value,
      cc: this.formGroup.get('cc')?.value,
      subject: this.formGroup.get('subject')?.value,
      description: this.formGroup.get('description')?.value
    };
    this.showMailTemplate = false;
    let mailData = {
      sid: this.isUpdate ? this.sid : uuidv4(),
      toRecipients: this.getActorDataJSON(formData.to, true),
      ccRecipients: this.getActorDataJSON(formData.cc),
      subject: formData.subject,
      appId: this.appGlobalService.get('appId'),
      orgId: this.appGlobalService.get('orgId'),
      ...insertFlds
    };
    if (this.isUpdate) {
      if (mailData.toRecipients !== this.mailObj.toRecipients) {
        mailData = { ...mailData, ...{ oldToRecipients: this.mailObj.toRecipients } };
      }
      if (mailData.ccRecipients !== this.mailObj.ccRecipients) {
        mailData = { ...mailData, ...{ oldCcRecipients: this.mailObj.ccRecipients } };
      }
    }
    this.subjectText = formData.subject;
    if (!this.dataFrom) {
      mailData = {
        ...mailData,
        ...{
          stepId: this.element.sid,
          workflowId: this.element.workflowId,
          description: formData.description
        }
      };
      const apiConstants = this.isUpdate ? ApiConstants.updateWorkflowMail : ApiConstants.saveWorkflowMail;
      console.log(mailData);
      this.store.dispatch(
        saveWorkflowMail({
          apiConstants,
          workflowMailData: mailData
        })
      );
    } else {
      mailData = {
        ...mailData,
        ...{
          entityId: this.element.sid,
          type: this.dataFrom,
          body: formData.description,
          tableId: this.formDetails?.sid,
          importName: this.element.name
        }
      };
      const apiConstants = this.isUpdate ? ApiConstants.updateImpMail : ApiConstants.saveImpMail;
      console.log(mailData);
      this.store.dispatch(
        saveImpMail({
          apiConstants,
          impMailData: mailData
        })
      );
    }
    if (!this.isUpdate) {
      this.opValue.emit(mailData.sid);
    }
    this.formGroup.reset();
  }

  closeDialog() {
    this.showMailTemplate = false;
    this.message = '';
    this.messages = [];
    this.errorFlag = false;
    this.formGroup.reset();
    this.warning = '';
    this.warnFlag = false;
  }

  onDescriptionInput(event: Event) {
    const target = event.target as HTMLElement;
    this.descriptionValueText = target.innerText;
    this.formGroup.get('description')?.setValue(target.innerHTML);
    this.formGroup.markAsDirty();
  }

  setValueToMail(obj) {
    // ngselect value is not binding immediately when the value is set, so used settimeout for proper binding
    setTimeout(() => {
      if (obj) {
        if (this.formGroup) {
          this.formGroup.patchValue({
            to: obj.toRecipients ? obj.toRecipients : null,
            cc: obj.ccRecipients ? obj.ccRecipients : null,
            subject: obj.subject ? obj.subject : this.subjectText,
            description: obj.description ? obj.description : this.descriptionText
          });
        }
        if (obj.subject) {
          this.subjectText = obj.subject;
        }
      } else {
        this.formGroup.patchValue({
          to: this.nextActor ? this.nextActor : null,
          cc: null,
          subject: this.subjectText,
          description: this.descriptionText
        });
      }
      // Parse description and insert chips as Quill embeds
      const desc = this.formGroup.get('description')?.value || '';
      // Reset lastIndex for global regex reuse
      GlobalConstants.variablePatternGlobal.lastIndex = 0;
      let lastIndex = 0;
      if (this.quill) {
        this.quill.setText('');
        let match;
        while ((match = GlobalConstants.variablePatternGlobal.exec(desc)) !== null) {
            // Insert text before chip
            if (match.index > lastIndex) {
              const text = desc.substring(lastIndex, match.index);
              this.quill.insertText(this.quill.getLength() - 1, text);
            }
            // Insert chip embed
            this.quill.insertEmbed(this.quill.getLength() - 1, 'chip', match[1], Quill.sources.USER);
            lastIndex = GlobalConstants.variablePatternGlobal.lastIndex;
          }
          // Insert any remaining text after last chip
          if (lastIndex < desc.length) {
            this.quill.insertText(this.quill.getLength() - 1, desc.substring(lastIndex));
        }
      }
      this.ref.detectChanges();
    }, 100);
  }

  addCustomUser = (term) => {
    if (
      /^(?![.!#$%&'*+/=?^_`{|}~-])(?!.*[!#$%&'*+/=?^_`{|}~.-]{2,})[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(
        term
      )
    ) {
      return term;
    }

    this.showMessage('error', 'Enter a valid email ID !');
  };

  showMessage(status: string, message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail
    });
  }

  setAsVariable() {
    let sel, range, html;
    if (window.getSelection) {
      sel = window.getSelection();
      if (sel.getRangeAt && sel.rangeCount) {
        range = sel.getRangeAt(0);

        html = `\${${range}}`;
        range.deleteContents();

        const el = document.createElement('div');
        el.innerHTML = html;
        const frag = document.createDocumentFragment();
        let node;
        let lastNode;
        while ((node = el.firstChild)) {
          lastNode = frag.appendChild(node);
        }
        range.insertNode(frag);
      }
    } else if (document.createRange) {
      range = document.createRange();
      range.collapse(false);
      range.pasteHTML(html);
    }
    return false;
  }

  ngOnDestroy(): void {
    this.store.dispatch(setWorkflowMail({ workflowMail: null }));
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  getSelectionHtml() {
    let sel, range, html;
    if (window.getSelection) {
      sel = window.getSelection();
      const selectedTextCount = sel.extentOffset - sel.anchorOffset;
      if (sel.getRangeAt && selectedTextCount > 1) {
        range = sel.getRangeAt(0);

        html = `\${${range}}`;
        range.deleteContents();

        const el = document.createElement('div');
        el.innerHTML = html;
        let frag = document.createDocumentFragment(),
          node,
          lastNode;
        while ((node = el.firstChild)) {
          lastNode = frag.appendChild(node);
        }
        range.insertNode(frag);
      }
    } else if (document.createRange) {
      range = document.createRange();
      range.collapse(false);
      range.pasteHTML(html);
    }
  }
  ngAfterViewInit() {
    setTimeout(() => {
      this.iconLoaded = true;
    }, 0);
  }
}
